/**
 * presets.ts — shared Framer Motion variants for primitive entrance/exit moments.
 *
 * Import the preset you need and spread it onto a `motion.<element>`:
 *
 *   import { entranceSpring } from '@nula/core-ui';
 *   <motion.span {...entranceSpring}>chip</motion.span>
 *
 * For exit animations, the consumer must wrap the rendered list with
 * `<AnimatePresence>` so that motion can keep the element mounted while
 * it animates out:
 *
 *   <AnimatePresence>
 *     {tags.map(t => <Tag key={t.id}>{t.label}</Tag>)}
 *   </AnimatePresence>
 */

/**
 * Spring scale-in/out for elements that appear or disappear dynamically
 * (chips being added, avatars joining presence stacks, badges, etc.).
 * Pair with `<AnimatePresence>` on the parent to get the exit animation.
 */
export const entranceSpring = {
  initial: { opacity: 0, scale: 0.6 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.6 },
  transition: { type: 'spring' as const, stiffness: 500, damping: 30 },
};

/** Sliding active-indicator preset for tab-bar-style components.
 *  Compose on a motion.div with layoutId pointing to a stable id. */
export const slidingIndicator = {
  layout: true as const,
  transition: { type: 'spring' as const, stiffness: 380, damping: 30 },
};

/** Staggered entrance for list-like items inside menu/dropdown.
 *  Cap at index 10 — beyond that the compound delay exceeds the
 *  parent menu's perceived open time. */
export const staggerItem = (index: number) => ({
  initial: { opacity: 0, y: -4 },
  animate: { opacity: 1, y: 0 },
  transition: {
    duration: 0.15,
    delay: Math.min(index, 10) * 0.02,
  },
});

/** Modal/Drawer entrance — decelerate spring with subtle Y offset.
 *  Feels deliberate vs the previous abrupt scale+fade. */
export const dialogEntrance = {
  initial: { opacity: 0, scale: 0.96, y: 8 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.96, y: 8 },
  transition: { type: 'spring' as const, stiffness: 340, damping: 32, mass: 0.9 },
};

/** Sonner/macOS-style toast entrance — springs UP from the corner
 *  it's anchored at. Initial Y offset is generous; spring lands it. */
export const toastEntrance = {
  initial: { opacity: 0, y: 24, scale: 0.92 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 24, scale: 0.92 },
  transition: { type: 'spring' as const, stiffness: 420, damping: 30 },
};

/** Drawer slide transition — spring-driven slide (replaces the old tween).
 *  Pair with placement-specific x/y initial/animate/exit values supplied
 *  by the Drawer component (the slide axis depends on placement). */
export const drawerSlide = {
  transition: { type: 'spring' as const, stiffness: 360, damping: 36 },
};
